import {isNil, merge, omitBy, some} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ConfirmModal, notifier, Tab, Tabs} from 'components';
import {usePrevious} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {MediaDevicesStorageData} from 'services';
import {logger, mediaDevicesUtils} from 'utils';

import {AudioInputDevice} from './AudioInputDevice';
import {AudioOutputDevice} from './AudioOutputDevice';
import {VideoInputDevice} from './VideoInputDevice';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.MEDIA_DEVICES_MODAL;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;

type Props = {
    open: boolean;
    mediaDevices?: MediaDevicesStorageData;
    onClose?: () => void;
    onChange?: (value: MediaDevicesStorageData) => void;
}

export const MediaDevicesDialog: React.FC<Props> = ({open, mediaDevices, onClose, onChange}) => {
    const {t} = useTranslation();

    const prevOpen = usePrevious(open);
    const [localDevices, setLocalDevices] = useState<MediaDeviceInfo[]>([]);
    const currentMediaDevices = useMemo(() => mediaDevicesUtils.getSelectedDevicesOrDefault(localDevices, mediaDevices),
        [localDevices, mediaDevices]);
    const [selectedDevices, setSelectedDevices] = useState<MediaDevicesStorageData>(mediaDevices);

    const isApplyDisabled = useMemo(() =>
        !some(selectedDevices, (value, key) => currentMediaDevices?.[key] !== value),
    [currentMediaDevices, selectedDevices]);

    const requestDevicePermissions = useCallback(() => {
        navigator.mediaDevices.getUserMedia({audio: true, video: true})
            .then((stream) => navigator.mediaDevices.enumerateDevices()
                .then((devices) => {
                    setLocalDevices(mediaDevicesUtils.getMediaDevices(devices, t));
                    mediaDevicesUtils.cleanMediaStream(stream);
                })
                .catch((error) => {
                    logger.error(error);
                    notifier.error({title: t(T_KEYS.DEVICES_LOAD_ERROR), details: error.message});
                }))
            .catch(((error) => {
                logger.error(error);
                notifier.error({title: t(T_KEYS.DEVICES_LOAD_ERROR), details: error.message});
            }));
    }, [t]);

    const getSelectedDeviceByKind = useCallback((kind: MediaDeviceKind) =>
        selectedDevices?.[kind] || currentMediaDevices?.[kind], [currentMediaDevices, selectedDevices]);

    const setSelectedDeviceByKind = useCallback((deviceId: string, kind: MediaDeviceKind) => {
        setSelectedDevices((prev) => ({...prev, [kind]: deviceId}));
    }, []);

    const resetValues = useCallback(() => {
        setSelectedDevices(currentMediaDevices);
    }, [currentMediaDevices]);

    const handleCloseModal = useCallback((confirmed: boolean) => {
        if (confirmed) {
            const newSelected = merge(mediaDevices, omitBy(selectedDevices, isNil));
            onChange(newSelected);
            notifier.success({title: t(T_KEYS.DEVICES_UPDATED)});
        } else {
            resetValues();
        }
        onClose();
    }, [onClose, mediaDevices, selectedDevices, onChange, t, resetValues]);

    useEffect(() => {
        if (open && !prevOpen) {
            navigator.mediaDevices.addEventListener('devicechange', requestDevicePermissions);
        }
        if (!open && prevOpen) {
            navigator.mediaDevices.removeEventListener('devicechange', requestDevicePermissions);
        }
    }, [open, prevOpen, requestDevicePermissions]);

    useEffect(() => {
        if (open && !prevOpen) {
            requestDevicePermissions();
        }
    }, [prevOpen, open, requestDevicePermissions]);

    useEffect(() => {
        setSelectedDevices(currentMediaDevices);
    }, [currentMediaDevices]);

    return (
        <ConfirmModal
            open={open}
            title={t(T_KEYS.TITLE)}
            okText={t(T_KEYS_COMMON.BUTTONS.APPLY)}
            okDisabled={isApplyDisabled}
            fullWidth
            maxWidth='xs'
            onClose={handleCloseModal}
        >
            <Tabs stretch contentIndent={false} defaultTabName={t(T_KEYS.MICROPHONE)}>
                <Tab name={t(T_KEYS.MICROPHONE)} icon='microphone-on'>
                    <AudioInputDevice
                        value={getSelectedDeviceByKind('audioinput')}
                        devices={localDevices}
                        onChange={(deviceId) => setSelectedDeviceByKind(deviceId, 'audioinput')}
                    />
                </Tab>
                <Tab name={t(T_KEYS.SPEAKER)} icon='speaker'>
                    <AudioOutputDevice
                        value={getSelectedDeviceByKind('audiooutput')}
                        devices={localDevices}
                        onChange={(deviceId) => setSelectedDeviceByKind(deviceId, 'audiooutput')}
                    />
                </Tab>
                <Tab name={t(T_KEYS.CAMERA)} icon='video-on'>
                    <VideoInputDevice
                        value={getSelectedDeviceByKind('videoinput')}
                        devices={localDevices}
                        onChange={(deviceId) => setSelectedDeviceByKind(deviceId, 'videoinput')}
                    />
                </Tab>
            </Tabs>
        </ConfirmModal>
    );
};
